/**
 * Application-Wide Constants and Type Definitions
 *
 * Defines the canonical sets of allowed values for key domain enums used
 * throughout the frontend. These arrays are declared `as const` so that
 * TypeScript can derive narrow union types from them.
 *
 * IMPORTANT: These values must stay in sync with the corresponding
 * database enum columns and Laravel validation rules in the backend.
 */

/**
 * Valid statuses for a work order, representing its lifecycle from creation to closure.
 *
 * Canonical lifecycle (mirrors WorkOrder::STATUSES in backend/app/Models/WorkOrder.php
 * and the 2026_06_07 add_review_approval_to_jobs migration):
 *
 *	 pending -> in_progress -> in_review -> approved -> completed
 *
 *	 - pending:	    created, work not started (database default)
 *	 - in_progress: work underway; may jump straight to completed when the
 *	                assignee self-signs-off (no separate verifier)
 *	 - in_review:   handed off for verification/supervisor review
 *	 - approved:    reviewer signed off on the FINISHED work. This is a
 *	                post-review state — estimate/cost approval before work
 *	                begins is a separate mechanism (estimate_approved_* fields
 *	                + EstimateApprovalController), not a status.
 *	 - completed:   done; completion_date is stamped
 *
 * Extension states (the status column is a plain string, so these need no
 * migration):
 *	 - on_hold:  pause; in_progress <-> on_hold
 *	 - invoiced -> closed: post-completion billing tail, entered from completed
 *	 - cancelled: terminal, reachable from any active status
 */
export const WORK_ORDER_STATUSES = [
	'pending',
	'in_progress',
	'in_review',
	'approved',
	'completed',
	'on_hold',
	'invoiced',
	'closed',
	'cancelled',
] as const;

/** Union type derived from WORK_ORDER_STATUSES for type-safe status references */
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

/**
 * Valid statuses for an inspection.
 *
 * Lifecycle flow:
 *	 scheduled -> in_progress -> completed or failed
 *	 (any status) -> cancelled
 */
export const INSPECTION_STATUSES = [
	'scheduled',
	'in_progress',
	'completed',
	'failed',
	'cancelled',
] as const;

/** Union type derived from INSPECTION_STATUSES for type-safe status references */
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

/**
 * Billing types that determine how a work order is priced and invoiced.
 *
 * - fixed_price: Flat rate agreed upon upfront
 * - time_and_materials: Billed based on actual hours and materials used
 * - not_to_exceed: Time and materials with a maximum cap
 * - unit_price: Billed per unit of work (e.g., per square foot)
 */
export const BILLING_TYPES = [
	'fixed_price',
	'time_and_materials',
	'not_to_exceed',
	'unit_price',
] as const;

/** Union type derived from BILLING_TYPES */
export type BillingType = (typeof BILLING_TYPES)[number];

/**
 * Billing-classification tags stored on a work order's / project work order's
 * `billing_info` array — the field the job forms actually write, and the one
 * the work orders list "Billing Type" filter matches against server-side.
 *
 * Distinct from BILLING_TYPES above: those are the (currently unwritten)
 * billing_type enum slugs, whereas these are the free-form tags operators tick
 * in the Billing section. This list used to be copy-pasted into JobForm and
 * ProjectWorkOrderCreatePage with a "keep these in sync" comment; it lives here
 * now so the forms that write the tags and the filter that reads them can never
 * drift apart again.
 */
export const BILLING_INFO_TAGS = [
	'Billable',
	'Non Billable',
	'Recurring',
	'T&M',
	'PW',
	'PW OT',
	'Davis Bacon',
	'Biohazard Cleanup',
	'Regular Wage',
] as const;

/** Union type derived from BILLING_INFO_TAGS */
export type BillingInfoTag = (typeof BILLING_INFO_TAGS)[number];

/**
 * Business lines (service categories) that the company operates in.
 * Each work order and location is associated with a business line.
 */
export const BUSINESS_LINES = [
	'janitorial',
	'construction',
	'maintenance',
	'landscaping',
	'snow_removal',
	'security',
] as const;

/** Union type derived from BUSINESS_LINES */
export type BusinessLine = (typeof BUSINESS_LINES)[number];

/**
 * Sub-types for construction work orders, providing further classification
 * when the business_line is "construction".
 */
export const CONSTRUCTION_SUB_TYPES = [
	'renovation',
	'buildout',
	'repair',
	'demolition',
	'new_construction',
] as const;

/** Union type derived from CONSTRUCTION_SUB_TYPES */
export type ConstructionSubType = (typeof CONSTRUCTION_SUB_TYPES)[number];

/**
 * Sub-types for janitorial work orders. T&M is the only value today;
 * the column is a loose string so additional values can be added here
 * without a backend migration. Mirrors the CONSTRUCTION_SUB_TYPES enum
 * so the form can drive subtype-aware behavior (e.g. switching the
 * assignee UI into per-employee hour-allocation mode).
 */
export const JANITORIAL_SUB_TYPES = [
	't_and_m',
] as const;

/** Union type derived from JANITORIAL_SUB_TYPES */
export type JanitorialSubType = (typeof JANITORIAL_SUB_TYPES)[number];

/**
 * User roles that control access and permissions throughout the application.
 *
 * - super_admin: Full platform access, can impersonate any user, access all companies/billing
 * - admin: Runs the companies they belong to (company-scoped on the server); every Admin tab but Billing
 * - manager: Can create/assign work orders and inspections within their company
 * - technician: Field worker who completes assigned work orders
 * - reviewer: Read-only access for auditing and quality control
 * - developer: Sees every company like a super_admin, plus API key management; no billing or impersonation
 * - company_admin: Manages users within their company, billing/invoice views
 * - subcontractor: External vendor, assignable like a technician
 * - csr: Construction Service Representative — sees only their own assignments
 * - jsr: Janitorial Service Representative — sees only their own assignments
 * - other: Catch-all field role — sees only their own assignments
 */
export const USER_ROLES = [
	'super_admin',
	'admin',
	'manager',
	'technician',
	'reviewer',
	'developer',
	'company_admin',
	'subcontractor',
	'csr',
	'jsr',
	'other',
] as const;

/** Union type derived from USER_ROLES */
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Roles whose people can be created from a first and last name alone.
 *
 * CSR / JSR / Other are people you assign to a job, not platform accounts, so
 * email, password, phone, and primary company are all optional for them. Email
 * is the login identifier, so one created without an address simply cannot
 * sign in — they exist as an assignable name.
 *
 * Mirrors Role::SIGN_IN_OPTIONAL_ROLES in the backend, which is what actually
 * enforces this; the frontend copy exists so the create/edit forms stop
 * demanding an email the API would have accepted as null.
 */
export const SIGN_IN_OPTIONAL_ROLES: readonly string[] = ['csr', 'jsr', 'other'];

/**
 * True when every selected role is sign-in optional — i.e. this person needs
 * nothing but a name. The check is "all", not "any": pairing CSR with, say,
 * manager produces a real operator account, and that account needs an email.
 */
export function rolesAllowMissingCredentials(roles: readonly string[]): boolean {
	const selected = roles.filter(Boolean);
	return selected.length > 0 && selected.every((role) => SIGN_IN_OPTIONAL_ROLES.includes(role));
}

/**
 * Roles that get the "Notify users" prompt before a work order or inspection
 * save — and, with it, the ability to drop recipients from that save.
 *
 * Deciding who hears about a change is an admin call: a manager reassigning a
 * job shouldn't be able to do it without the assignee finding out, and the
 * technician-facing app has no business suppressing the notifications their
 * supervisors rely on. 'developer' is an internal super-user account, not a
 * separate product role, so it sits with the admins.
 *
 * Mirrors NotifyDirective::ADMIN_ROLES in the backend, which is what actually
 * enforces this — the server ignores the flags from anyone else, whatever the
 * client posts.
 */
export const NOTIFY_CONTROL_ROLES = ['super_admin', 'admin', 'developer'] as const;

/**
 * Roles allowed to create, edit, or deactivate a building (location).
 *
 * Gates both surfaces that can create one: the "Add Building" button on the
 * Buildings page and the "Add a New Building" link under the Building
 * dropdown on the work order / inspection create form. Field-tier roles
 * (technician, reviewer, subcontractor, csr/jsr/other) READ locations — they
 * appear in job forms and the Buildings list — but must never create one.
 *
 * Mirrors LocationController::authorizeManageLocations() in the backend,
 * which is what actually enforces this; hiding the button just keeps a
 * technician from being handed a form the API would reject with a 403.
 */
export const LOCATION_MANAGE_ROLES = ['super_admin', 'admin', 'manager', 'developer', 'company_admin'] as const;

/**
 * Display names for role slugs whose Title-Cased slug reads badly.
 *
 * formatLabel('csr') would render "Csr"; acronyms need spelling out so an
 * admin picking a role knows what CSR and JSR actually stand for. Anything
 * not listed here falls through to the generic slug formatter.
 */
export const ROLE_LABELS: Record<string, string> = {
	csr: 'CSR (Construction Service Rep)',
	jsr: 'JSR (Janitorial Service Rep)',
};

/**
 * One-line explanations of what each role can actually do, shown under the
 * role's name in the Admin "Assign Roles" picker.
 *
 * The role names alone don't separate the roles that matter most to get right:
 * "reviewer" vs "manager" vs "company_admin" all read like plausible admins,
 * and the difference — read-only, creates jobs, runs their own company's
 * billing — only lives in the permission checks. Spelling it out at the point
 * of choice is cheaper than discovering the mistake when someone can't open an
 * invoice, or can.
 *
 * Kept alongside ROLE_LABELS rather than inline in the form so the next screen
 * that lists roles (the role filter, the edit-user drawer) gets the same
 * wording instead of inventing its own. Anything not listed here simply
 * renders without a description.
 */
export const ROLE_DESCRIPTIONS: Record<string, string> = {
	super_admin: 'Full platform access — every company, all billing, and user impersonation.',
	admin: 'Runs the work for the companies they belong to: users, jobs, inspections, and buildings.',
	manager: 'Creates, assigns, and closes work orders and inspections for their company.',
	technician: 'Field worker. Sees and completes the jobs assigned to them.',
	reviewer: 'Read-only access for auditing and quality control. Changes nothing.',
	developer: 'Sees every company, plus API keys and integration tooling. Internal accounts.',
	company_admin: 'Manages the users, billing, and invoices for their own company only.',
	subcontractor: 'Outside vendor. Assignable to jobs like a technician, scoped to their own.',
	csr: 'Construction-side rep. Assignable by name, sees only their own jobs; email optional.',
	jsr: 'Janitorial-side rep. Assignable by name, sees only their own jobs; email optional.',
	other: 'Catch-all for anyone you need to assign by name. Sees only their own jobs.',
};

/**
 * The users.role ENUM values — the denormalized label column, which has no slot
 * for 'super_admin' or 'company_admin'.
 *
 * Roles themselves live in the user_roles junction table; this column is only
 * the single label the user list, the role filter, and the assignee picker's
 * context line read from.
 */
export const ROLE_LABEL_COLUMN_VALUES: readonly string[] = [
	'admin',
	'manager',
	'technician',
	'reviewer',
	'subcontractor',
	'csr',
	'jsr',
	'other',
];

/** Fallback for the users.role label column when no assigned role can fill it. */
export const DEFAULT_USER_ROLE_LABEL = 'technician';

/**
 * Pick the users.role label for a set of assigned roles: the first checked role
 * the ENUM can actually store, falling back to whatever the form already had.
 *
 * Without this the singular column keeps its 'technician' default and the newer
 * roles never surface in the role filter or the assignee picker — or, worse, a
 * surface sends 'company_admin' straight into a column that cannot hold it.
 * Shared so both create-user forms file a new user under the same label.
 */
export function primaryRoleLabel(roles: readonly string[], fallback = DEFAULT_USER_ROLE_LABEL): string {
	return roles.find((role) => ROLE_LABEL_COLUMN_VALUES.includes(role)) ?? fallback;
}

/**
 * The roles a brand-new user starts with in every create-user form.
 *
 * Reviewer is the least-privileged role in the system — read-only, changes
 * nothing — so an admin who saves without touching the picker cannot
 * accidentally hand out more access than they meant to.
 */
export const DEFAULT_ASSIGNED_ROLES: readonly string[] = ['reviewer'];

/* ============================================
 * ASSIGNMENT — the values the Assignment section chooses from
 * ============================================
 * Shared by the work order / inspection form (JobForm) and the
 * assign-from-the-list dialog (AssignWorkOrderModal), so the two
 * surfaces offer the same priorities and the same timezones. */

/**
 * Sub-type slug that forces hour tracking on.
 *
 * Seeded as a system row under both Janitorial and Construction, and
 * re-usable under any CMS-added line. A T&M job is billed on time, so its
 * hours aren't optional: the "Require Hours" checkbox renders ticked and
 * disabled, and the hour bucket becomes mandatory. Mirrors
 * WorkOrder::T_AND_M_SUB_TYPE on the server, which enforces the same rules
 * regardless of what the client posts.
 */
export const T_AND_M_SUB_TYPE = 't_and_m';

/** Priority dropdown options — value is the enum stored on the record. */
export const WORK_ORDER_PRIORITY_OPTIONS = [
	{ value: 'low', label: 'Low' },
	{ value: 'medium', label: 'Medium' },
	{ value: 'high', label: 'High' },
	{ value: 'urgent', label: 'Urgent' },
] as const;

/**
 * Common US timezone options for the scheduling timezone override
 * dropdown. The user's profile timezone (from AuthContext) is the default;
 * these let the user override on a per-job basis when scheduling across
 * time zones. Values are IANA timezone identifiers so the frontend can
 * convert to UTC before sending.
 */
export const SCHEDULE_TIMEZONE_OPTIONS = [
	{ value: 'America/New_York', label: 'Eastern Time (ET)' },
	{ value: 'America/Chicago', label: 'Central Time (CT)' },
	{ value: 'America/Denver', label: 'Mountain Time (MT)' },
	{ value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
	{ value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
	{ value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
	{ value: 'America/Phoenix', label: 'Arizona (no DST)' },
] as const;
/**
 * Roles allowed to create users from inside the app — the header's "Add new
 * user" button and the hours editor's "Quick Add User" link.
 *
 * Which ROLES each of these may hand out is narrower and decided server-side
 * (UserController::getAllowedRolesForUser, surfaced by GET
 * /users/available-roles) — a manager can add a technician but not a CSR, for
 * example. This list only decides whether the entry point shows at all, so a
 * technician is never handed a form the API would refuse outright.
 */
export const USER_CREATE_ROLES = ['manager', 'admin', 'super_admin', 'company_admin', 'developer'] as const;

/**
 * Roles that may open the CMS at all. Admins were added in Oct 2026 (beta
 * feedback: "allow the Admin level to access and create templates under CMS
 * Checklists") — mirrors the AdminOrDeveloper middleware on the backend's
 * /checklists routes.
 */
export const CMS_ROLES = ['super_admin', 'admin', 'developer'] as const;

/**
 * Roles that see the WHOLE CMS. Everyone else in CMS_ROLES gets only the
 * Checklists and Areas tabs; Photo Types and Business Lines writes are
 * super-admin only server-side, so showing them would only offer saves that
 * come back 403.
 */
export const CMS_FULL_ROLES = ['super_admin'] as const;

/**
 * Roles that may create a work order (New Order, Duplicate). Technicians were
 * added in Oct 2026 (beta feedback: "We need Technician, Manager, Admin and
 * Super Admin to be able to create work orders") so a tech on site can book
 * extra work. Editing and deleting stay manager-tier. Mirrors
 * WorkOrder::CREATE_ROLES on the backend.
 */
export const WORK_ORDER_CREATE_ROLES = ['super_admin', 'admin', 'manager', 'technician', 'developer'] as const;
